import type { JSX } from "react";
import { Image, Text, View } from "@/components";
import { resolveFileSrc } from "@/utils/elearning/fileUrl";
import {
  type ImageSize,
  type InlineNode,
  type MarkdownImage,
  parseInline,
  parseMarkdown
} from "@/utils/elearning/markdown";

export interface MarkdownContentProps {
  /** Markdown acotado (ver `utils/elearning/markdown`). */
  content: string;
  className?: string;
}

/**
 * Ancho de una imagen suelta según su `size=`. Valores arbitrarios a propósito: el
 * atom `Image` traduce clases `w-N`/`h-N` con su enum de tamaños y rompería el width.
 */
const WIDTH_BY_SIZE: Record<ImageSize, string> = {
  sm: "w-full sm:w-[33%]",
  md: "w-full sm:w-[50%]",
  lg: "w-full sm:w-[75%]",
  full: "w-full"
};

/** Alto máximo de las miniaturas de una galería (mismo criterio que la imagen suelta). */
const GALLERY_HEIGHT_BY_SIZE: Record<ImageSize, string> = {
  sm: "max-h-[6rem]",
  md: "max-h-[10rem]",
  lg: "max-h-[14rem]",
  full: "max-h-[20rem]"
};

/**
 * Jerarquía de subtítulos dentro de una sección: `#`/`##` es el subtítulo principal
 * (más grande y con aire arriba), `###` el intermedio y `####` el menor. El título de
 * la sección lo pone el contenedor (resumen del módulo), por encima de todos.
 */
const HEADING_CLASS: Record<1 | 2 | 3 | 4, string> = {
  1: "text-lg font-semibold text-foreground pt-4 first:pt-0",
  2: "text-lg font-semibold text-foreground pt-4 first:pt-0",
  3: "text-base font-semibold text-foreground pt-3 first:pt-0",
  4: "text-sm font-semibold uppercase tracking-wide text-foreground/80 pt-2 first:pt-0"
};

const renderInline = (nodes: InlineNode[]): JSX.Element[] =>
  nodes.map((node, index) => {
    const key = index;
    if (node.kind === "strong")
      return (
        <strong key={key} className="font-semibold text-foreground">
          {node.text}
        </strong>
      );
    if (node.kind === "em")
      return (
        <em key={key} className="italic">
          {node.text}
        </em>
      );
    return <span key={key}>{node.text}</span>;
  });

const Figure = ({
  image,
  className
}: {
  image: MarkdownImage;
  className: string;
}): JSX.Element => (
  <figure className={`flex flex-col items-center gap-1.5 m-0 ${className}`}>
    <Image
      src={resolveFileSrc(image.url)}
      alt={image.caption}
      width={800}
      height={600}
      unoptimized
      className="max-w-full w-auto h-auto max-h-[28rem] rounded-xl border border-border bg-white object-contain"
    />
    {image.caption && (
      <Text type="span" className="text-xs text-muted-foreground text-center">
        {image.caption}
      </Text>
    )}
  </figure>
);

/**
 * @name MarkdownContent
 * @description Renderiza el contenido de una sección del resumen escrito en el
 * Markdown acotado del proyecto: párrafos, subtítulos, listas, negrita/cursiva e
 * imágenes en línea con su tamaño (`size=sm|md|lg|full`, centradas) y galerías
 * (imágenes en renglones consecutivos, en una fila centrada que envuelve). Las
 * imágenes se sirven por el proxy autenticado (`resolveFileSrc`). No interpreta
 * HTML: todo lo que no es del subconjunto se muestra como texto.
 *
 * @param {MarkdownContentProps} props - Markdown y clase opcional del contenedor.
 * @returns {JSX.Element} El contenido renderizado.
 */
const MarkdownContent = ({
  content,
  className = ""
}: MarkdownContentProps): JSX.Element => {
  const blocks = parseMarkdown(content);

  return (
    <View className={`flex flex-col gap-3 ${className}`}>
      {blocks.map((block, index) => {
        const key = index;
        switch (block.kind) {
          case "heading":
            return (
              <Text
                key={key}
                type={block.level <= 2 ? "h3" : "h4"}
                className={HEADING_CLASS[block.level]}
              >
                {block.text}
              </Text>
            );
          case "list":
            return (
              <ul
                key={key}
                className="list-disc pl-5 flex flex-col gap-1.5 text-sm leading-relaxed text-muted-foreground"
              >
                {block.items.map((item, itemIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: ítems de texto sin id
                  <li key={itemIndex}>{renderInline(parseInline(item))}</li>
                ))}
              </ul>
            );
          case "orderedList":
            // Sin viñeta: el marcador del material (`a.`, `1.`) se muestra tal cual,
            // con sangría francesa para que el texto que envuelve quede alineado.
            return (
              <ol
                key={key}
                className="pl-1 flex flex-col gap-1.5 text-sm leading-relaxed text-muted-foreground"
              >
                {block.items.map((item, itemIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: ítems de texto sin id
                  <li key={itemIndex} className="flex gap-2">
                    <span className="shrink-0 font-medium text-foreground">
                      {item.marker}
                    </span>
                    <span>{renderInline(parseInline(item.text))}</span>
                  </li>
                ))}
              </ol>
            );
          case "image":
            return (
              <View key={key} className="flex justify-center">
                <Figure
                  image={block.image}
                  className={WIDTH_BY_SIZE[block.image.size]}
                />
              </View>
            );
          case "gallery":
            return (
              <View
                key={key}
                className="flex flex-wrap justify-center items-end gap-3"
              >
                {block.images.map((image, imageIndex) => (
                  <figure
                    // biome-ignore lint/suspicious/noArrayIndexKey: la misma imagen puede repetirse
                    key={imageIndex}
                    className="flex flex-col items-center gap-1 m-0 max-w-full"
                  >
                    <Image
                      src={resolveFileSrc(image.url)}
                      alt={image.caption}
                      width={400}
                      height={300}
                      unoptimized
                      className={`w-auto h-auto max-w-full ${GALLERY_HEIGHT_BY_SIZE[image.size]} rounded-lg border border-border bg-white object-contain`}
                    />
                    {image.caption && (
                      <Text
                        type="span"
                        className="text-xs text-muted-foreground text-center"
                      >
                        {image.caption}
                      </Text>
                    )}
                  </figure>
                ))}
              </View>
            );
          default:
            return (
              <Text
                key={key}
                type="p"
                className="text-sm leading-relaxed text-muted-foreground"
              >
                {renderInline(parseInline(block.text))}
              </Text>
            );
        }
      })}
    </View>
  );
};

export default MarkdownContent;
